import { OktaAuth, AccessToken } from '@okta/okta-auth-js';

const oktaAuth = new OktaAuth({
  clientId: process.env.REACT_APP_OKTA_CLIENT_ID!,
  issuer: process.env.REACT_APP_OKTA_ISSUER!,
  redirectUri: process.env.REACT_APP_OKTA_REDIRECT_URI!,
  scopes: ['openid', 'profile', 'email'],
  pkce: true,
  responseType: 'code',
});

export async function handleLoginCallback(): Promise<string> {
  if (oktaAuth.isLoginRedirect()) {
    await oktaAuth.handleLoginRedirect();
    // Check localStorage first (set by sub-apps like /drc), then Okta state
    const storedUri = localStorage.getItem('rdds_original_uri');
    localStorage.removeItem('rdds_original_uri');
    const originalUri = storedUri || oktaAuth.getOriginalUri() || '/';
    oktaAuth.removeOriginalUri();
    return originalUri;
  }
  return '/';
}

export async function getAccessToken(): Promise<string | undefined> {
  const tokenManager = oktaAuth.tokenManager;
  const token = await tokenManager.get('accessToken');
  if (token && 'accessToken' in token && !tokenManager.hasExpired(token)) {
    return (token as AccessToken).accessToken;
  }
  try {
    const renewed = await tokenManager.renew('accessToken');
    if (renewed && 'accessToken' in renewed) {
      return (renewed as AccessToken).accessToken;
    }
    return undefined;
  } catch {
    return undefined;
  }
}

export async function validateSession(): Promise<boolean> {
  // Fast path: if the access token is in storage and not expired, we are good
  // without any network call. A previous version here ALWAYS called renew() on
  // every page load; since the Okta app does not request offline_access there
  // is no refresh token, so renew falls back to a hidden-iframe silent auth
  // using Okta's session cookie, which fails intermittently (third-party
  // cookie restrictions, rate limits, slow networks). That failure was then
  // caught below and cleared ALL tokens, bouncing the user back to Okta on
  // every refresh.
  try {
    const token = await oktaAuth.tokenManager.get('accessToken');
    if (!token) return false;
    if (!oktaAuth.tokenManager.hasExpired(token)) return true;
    // Only renew when the token is actually expired. If renew fails here we
    // still do not nuke tokens: let the caller decide (API 401 handler will
    // trigger a fresh sign-in flow explicitly when needed).
    const renewed = await oktaAuth.tokenManager.renew('accessToken');
    return !!renewed;
  } catch {
    return false;
  }
}

export async function isAuthenticated(): Promise<boolean> {
  return oktaAuth.isAuthenticated();
}

export async function getUser(): Promise<{ email: string; name: string } | null> {
  const userInfo = await oktaAuth.getUser();
  if (!userInfo) return null;
  return {
    email: (userInfo.email as string) || '',
    name: (userInfo.name as string) || (userInfo.preferred_username as string) || '',
  };
}

export async function signIn(originalUri?: string): Promise<void> {
  if (originalUri) {
    localStorage.setItem('rdds_original_uri', originalUri);
  }
  await oktaAuth.signInWithRedirect();
}

export async function signOut(): Promise<void> {
  await oktaAuth.signOut({
    postLogoutRedirectUri: window.location.origin + '/',
    clearTokensBeforeRedirect: true,
  });
}

export { oktaAuth };
